import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { isStaffSurface } from '@/lib/staffSurfaces';
import AnnouncementBar from './notices/AnnouncementBar';
import Navbar from './Navbar';

/**
 * SiteHeader — stacks AnnouncementBar + Navbar as a single stable fixed unit.
 *
 * Advanced fix for flicker/blink:
 * 1. AnnouncementBar wrapper reserves a fixed slot so the Navbar never shifts.
 * 2. Collapse uses CSS transform (translateY) instead of max-height/opacity
 *    for GPU-accelerated, jank-free animation with no layout reflow.
 * 3. The bar content is always mounted (never returns null during loading)
 *    — the inner AnnouncementBar handles its own empty state gracefully.
 * 4. will-change + containment hints avoid paint thrashing.
 */
export default function SiteHeader() {
  const { pathname } = useLocation();

  // NOTE: Do NOT add early returns above the hooks below.
  // React requires hooks to be called in the same order every render.

  const [collapsed, setCollapsed] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  const barHeightRef = useRef(0);

  // Measure the actual announcement bar height once it mounts
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const measure = () => {
      const h = el.scrollHeight;
      if (h !== barHeightRef.current) {
        barHeightRef.current = h;
        setBarHeight(h);
      }
    };

    // Initial measure
    measure();

    // Re-measure on resize and when content might change
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // no barHeight dependency — ref tracks it internally

  // Scroll handler — collapse announcement bar after 30px scroll
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setCollapsed(window.scrollY > 30);
        ticking = false;
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hide on every staff app shell — each has its own top bar and owns its
  // viewport, so the marketing chrome must not sit on top of it. The route list
  // lives in `staffSurfaces` because the chatbot has to make the same call, and
  // two copies of it drifted apart once already.
  // This MUST be placed AFTER all hook calls to satisfy React Rules of Hooks.
  if (isStaffSurface(pathname)) return null;

  return (
    <header className="sticky top-0 z-50 w-full [contain:layout]">
      {/* Announcement bar slot — uses transform for GPU animation, no layout reflow */}
      <div
        className="overflow-hidden will-change-transform announcement-bar-slot"
        style={{ height: collapsed ? 0 : barHeight || undefined }}
      >
        <div ref={barRef}>
          <AnnouncementBar />
        </div>
      </div>

      {/* Navbar — always stable, never shifts */}
      <Navbar />
    </header>
  );
}
